import { onUnmounted, ref } from 'vue'

/**
 * The camera QR reader (cordova-plugin-qrscanner, `window.QRScanner`), shared:
 *
 * - `prepareQrScanner`: permission + prepare. The passenger reader (components/staffStreamAuto.vue) calls it, then
 *   scans in a loop while the stream lasts.
 * - `useQrScan().start()`: one read with the back camera (the bus walkthrough). The preview renders
 *   behind the webview, so while it scans the page is hidden (`html.qr-scan-active`, components/QrScanOverlay.vue)
 *   and only the overlay shows. Resolves with the text, or null when cancelled or the camera is unavailable.
 *
 * Permission: `prepare` asks the system the first time. Once denied, the system never asks again: `cameraDenied` is
 * set and `openCameraSettings` opens the app's settings page, for the caller to offer as a button.
 */
export function prepareQrScanner(onReady: (status: any) => void, onError: (err: any, status?: any) => void) {
  const scanner = (window as any).QRScanner
  if (!scanner) {
    onError(new Error('QRScanner unavailable'))
    return
  }
  scanner.prepare((err: any, status: any) => {
    if (err) {
      onError(err, status)
    } else if (status?.authorized) {
      onReady(status)
    } else {
      onError(null, status)
    }
  })
}

export function useQrScan() {
  const scanning = ref(false)
  const error = ref<string | null>(null)
  /** The camera permission was denied before: only the phone's settings can give it now. */
  const cameraDenied = ref(false)
  let resolver: ((text: string | null) => void) | null = null

  const hidePreview = () => {
    if (!scanning.value) return
    scanning.value = false
    document.documentElement.classList.remove('qr-scan-active')
    const scanner = (window as any).QRScanner
    // destroy, not only hide: a scan closed from outside (the check dropped while scanning) left the camera running
    // behind the page. destroy hides the preview, cancels the scan and releases the camera (and its light).
    scanner?.destroy(() => {})
  }

  const finish = (text: string | null) => {
    hidePreview()
    const resolve = resolver
    resolver = null
    resolve?.(text)
  }

  const start = (): Promise<string | null> => new Promise((resolve) => {
    if (resolver) finish(null)
    error.value = null
    cameraDenied.value = false
    resolver = resolve
    prepareQrScanner(() => {
      const scanner = (window as any).QRScanner
      scanner.useBackCamera(() => {
        if (!resolver) return
        scanning.value = true
        document.documentElement.classList.add('qr-scan-active')
        scanner.show()
        scanner.scan((scanErr: any, text: string) => finish(scanErr ? null : (text || null)))
      })
    }, (err: any, status: any) => {
      const scanner = (window as any).QRScanner
      // Plugin error 1 = CAMERA_ACCESS_DENIED; `status.denied` = denied before (the system won't ask again).
      cameraDenied.value = !!scanner && (err?.code === 1 || !!status?.denied)
      error.value = !scanner
        ? 'Esta versión de la app no tiene lector de códigos.'
        : cameraDenied.value
          ? 'La app no tiene permiso para usar la cámara.'
          : 'No pudimos abrir la cámara.'
      finish(null)
    })
  })

  const stop = () => finish(null)

  /** The app's page in the phone's settings, where the camera permission is turned on. */
  const openCameraSettings = () => (window as any).QRScanner?.openSettings?.()

  onUnmounted(stop)

  return { scanning, error, cameraDenied, start, stop, openCameraSettings }
}

